import { h, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { clsx } from 'clsx';
import getLineItemAttributes from '../utils/storefront/get-attributes';
import ProductQuantity from './product-qty';
import Price from './product-price';
import { liqTrack } from '../utils/helpers';
import IconPlus from '../utils/icon/plus.svg';

const AddToCartButton = ({
  currentVariant,
  currentProduct,
  showQty,
  showPrice = false,
  isMargin = true,
  showMessage = true,
  promoMessage,
  btnStyle = 'btn-primary',
  btnFont = 'cta-large',
  btnPriceFont = 'cta-large',
  selectedKitItems,
  customClass = '',
  customButtonClass = '',
  multipleAdd = false,
  subscribeId,
  isGiftCard = false,
  formRef = false,
  showGiftSend = false,
  isProductCard = false,
  isKits = false,
  isRedirect = false,
  hidePriceMobile = false,
  customIsEnabled = true,
  customFunction,
  customPrice,
  customComparePrice,
  customButtonContent = '',
  customLabel = '',
  customAttributes = []
}) => {
  const openBis = new CustomEvent('bis:open');
  const openRise = new CustomEvent('rise:open');
  const submitRise = new CustomEvent('rise:submit', {
    detail: {
      form: formRef,
    },
  });
  const multipleAddText =
    customLabel ||
    window.TMW?.Strings?.product?.multipleAddToCart ||
    'add to cart';
  const singleAtcText =
    customLabel || window.TMW?.Strings?.product?.addToCart || 'add to cart';
  const oosText = window.TMW?.Strings?.product?.outOfStock || 'out of stock';
  const bisText = window.TMW?.Strings?.backinstock?.buttonText || 'notify me';
  const kitText = window.TMW?.Strings?.product?.kitText || 'select products';
  const redirectText =
    window.TMW?.Strings?.product?.viewProduct || 'view product';
  const subscribeText = window.TMW?.Strings?.product?.subscribe || 'subscribe';

  let sectionSelects = null;

  const [qty, setQty] = useState(1);
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);
  const [isKit, setIsKit] = useState(false);
  const [isKitAvailable, setIsKitAvailable] = useState(false);
  const [enableBISNotification, setEnableBISNotification] = useState(false);

  const buttonText = useRef(singleAtcText);

  /**
   * Add multiple items to the cart at once
   * doesnt support subscription or kits
   * @param {Array} products
   */
  const multipleAddToCart = (products) => {
    const multiplePayload = [];

    products.map((product) => {
      const variant =
        (product?.variants?.edges && product?.variants?.edges[0]?.node) ||
        product.variants[0];

      if (typeof variant.id !== 'string') {
        variant.id = String(variant.id)
      }

      const attributes = getLineItemAttributes({
        product,
        variant,
      });

      if (product.tags.includes('oversized')) {
        attributes.push({
          key: '_oversized',
          value: true,
        });
      }

      if (customAttributes.length) {
        customAttributes.forEach(attr => attributes.push(attr))
      }

      if (product.availableForSale)
        multiplePayload.push({
          id: variant.id,
          quantity: qty,
          attributes,
        });

      return '';
    });

    liqTrack(products, qty);

    const multipleAddToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: multiplePayload,
      },
    });
    document.dispatchEvent(multipleAddToCartEvent);
  };

  const addToCart = () => {
    const attributes = getLineItemAttributes({
      product: currentProduct,
      variant: currentVariant,
      selectedKitItems,
      subscribeId,
    });

    if (currentProduct.tags.includes('oversized')) {
      attributes.push({
        key: '_oversized',
        value: true,
      });
    }

    if (customAttributes.length) {
      customAttributes.forEach(attr => attributes.push(attr))
    }

    const cartAddPayload = {
      detail: {
        payload: {
          id: currentVariant?.id,
          quantity: parseInt(qty, 10),
          attributes,
        },
      },
    };

    liqTrack([currentProduct], qty);

    if (subscribeId) {
      // eslint-disable-next-line max-len
      cartAddPayload.detail.payload.sellingPlanId = `gid://shopify/SellingPlan/${subscribeId}`;
      cartAddPayload.detail.payload.selling_plan = parseInt(subscribeId, 10);
    }

    const addToCartEvent = new CustomEvent('cart:add', cartAddPayload);
    document.dispatchEvent(addToCartEvent);
  };

  useEffect(() => {
    const enableBIS = !(
      currentProduct?.disableBISNotification?.value === 'true' ||
      currentProduct?.disableBIS
    );
    setEnableBISNotification(enableBIS);

    // kits
    if (isKit && isKitAvailable) {
      buttonText.current = singleAtcText;
      setIsButtonEnabled(true);
      return;
    }
    if (isKit && !isKitAvailable && enableBIS && !isProductCard) {
      buttonText.current = bisText;
      setIsButtonEnabled(true);
      return;
    }

    if (isKits && isProductCard) {
      buttonText.current = kitText;
      setIsButtonEnabled(true);
      return;
    }

    // multiple atc
    if (multipleAdd) {
      buttonText.current = multipleAddText;
      setIsButtonEnabled(true);
      return;
    }

    if (isRedirect) {
      buttonText.current = redirectText;
      setIsButtonEnabled(true);
      return;
    }

    // subscription products
    if (subscribeId) {
      buttonText.current = subscribeText;
      setIsButtonEnabled(true);
      return;
    }

    // standard products
    if (!isKit && currentProduct?.availableForSale) {
      buttonText.current = singleAtcText;
      setIsButtonEnabled(true);
      return;
    }

    if (
      !isKit &&
      !currentProduct?.availableForSale &&
      enableBIS &&
      !isProductCard
    ) {
      buttonText.current = bisText;
      setIsButtonEnabled(true);
      return;
    }

    // default OOS state
    buttonText.current = oosText;
    setIsButtonEnabled(false);
  }, [
    currentProduct,
    currentVariant,
    isKit,
    isKitAvailable,
    multipleAdd,
    customLabel,
  ]);

  const handleKitOptionChange = () => {
    if (!sectionSelects || sectionSelects.length < 1) return;

    setIsKit(true);
    let isOos = false;

    [...sectionSelects].forEach((select) => {
      [...select.options].forEach((option) => {
        if (select.value === option.value && option.disabled) isOos = true;
      });
    });

    setIsKitAvailable(!isOos);
  };

  useEffect(() => {
    sectionSelects = document
      .querySelector('[data-section="product"]')
      ?.querySelectorAll('select[data-kit-select]');

    if (sectionSelects) {
      [...sectionSelects].forEach((select) => {
        // component can render multiple times and bind multiple change events
        select.removeEventListener('change', handleKitOptionChange);
        select.addEventListener('change', handleKitOptionChange);
      });
    }

    handleKitOptionChange();

    return () => {
      if (sectionSelects) {
        [...sectionSelects].forEach((select) => {
          select.removeEventListener('change', handleKitOptionChange);
        });
      }
    };
  }, []);

  const handleAtcButtonClick = (e) => {
    e.preventDefault();

    if (customFunction) {
      customFunction();
    }
    if (isGiftCard) {
      document.dispatchEvent(submitRise);
      return;
    }

    if ((isKits && isProductCard) || (isRedirect && isProductCard)) {
      window.location.href = `/products/${currentProduct?.handle}`;
      return;
    }

    if (multipleAdd) {
      multipleAddToCart(multipleAdd);
      return;
    }
    if (currentProduct?.availableForSale || (isKit && isKitAvailable)) {
      addToCart();
      return;
    }
    if (enableBISNotification) {
      document.dispatchEvent(openBis);
    }

    // do nothing if the above conditions are not satisfied
  };

  const currentPrice = customPrice
    ? { amount: customPrice }
    : multipleAdd
    ? {
        amount: multipleAdd
          ?.map((product) =>
            product.availableForSale
              ? product?.variants?.edges[0]?.node?.price?.amount
              : 0,
          )
          .reduce((prev, next) => parseFloat(prev) + parseFloat(next), 0),
        currencyCode: window.TMW?.localization?.currencyCode || 'USD',
      }
    : currentVariant?.price;

  const comparePrice = customComparePrice
    ? { amount: customComparePrice } : currentVariant?.compareAtPrice

  const isVoyageSProduct = currentProduct?.title
    .toLowerCase()
    .includes('voyages');
  const isBtnStyleDefault = btnStyle === 'btn-primary';

  const buttonStyle = isVoyageSProduct
    ? isBtnStyleDefault
      ? 'btn-primary-v'
      : `btn-primary-v ${btnStyle}`
    : btnStyle;

  return (
    <div
      className={clsx(
        showMessage && 'pb-8 border-b border-black-30',
        customClass,
      )}
    >
      {showGiftSend !== 'true' && (
        <div
          className={clsx(
            isMargin && 'mb-2.5',
            'flex items-center justify-start gap-[7.8px]',
          )}
        >
          {showQty && (
            <ProductQuantity
              quantity={qty}
              maxQuantity={currentVariant?.quantityAvailable}
              changeQuantity={(value) => setQty(value)}
            />
          )}
          <button
            type="submit"
            className={clsx(
              buttonStyle,
              btnFont,
              'py-[14.5px] w-full',
              showPrice && 'flex items-center justify-between',
              customButtonClass,
            )}
            disabled={!isButtonEnabled || !customIsEnabled}
            onClick={handleAtcButtonClick}
          >
            {customButtonContent ? (
              <span className="text-center block w-full">
                {customButtonContent}
              </span>
            ) : (
              <>
                <span
                  className={clsx(isProductCard ? 'hidden laptop:block' : '')}
                >
                  {buttonText.current}
                </span>
                {isProductCard && (
                  <span className="laptop:hidden">
                    <IconPlus />
                  </span>
                )}
                {showPrice && !isRedirect && (
                  <Price
                    currentPrice={currentPrice}
                    currentComparePrice={comparePrice}
                    fontClass={btnPriceFont}
                    customContainerClass={clsx(
                      hidePriceMobile ? 'hidden laptop:block' : '',
                    )}
                    comparePriceColor="text-white"
                  />
                )}
              </>
            )}
          </button>
        </div>
      )}
      {showGiftSend === 'true' && (
        <button
          onClick={() => document.dispatchEvent(openRise)}
          className="btn-primary cta-large py-[14.5px] w-full transition-all hover:!border-charcoal hover:bg-charcoal hover:!text-white"
        >
          {buttonText.current}
        </button>
      )}
      {showMessage && promoMessage && (
        <div
          className="bg-white py-[11px] px-[10px] utility-1 text-charcoal rte rounded-[4px] rte-single-line"
          dangerouslySetInnerHTML={{
            __html: promoMessage.replaceAll('\n', '<br>'),
          }}
        ></div>
      )}
    </div>
  );
};

export default AddToCartButton;
